import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EncField, Encoding, EncodingResult } from '@mode-vegas/vegas';
import { IconButtonComponent } from '@mode/capra';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { EncodingFieldPillComponent } from '../encoding-field/encoding-field-pill.component';
import { UpdateFieldService } from '../encoding-field/update-field.service';
import { UpdateSingleFieldService } from './update-single-field.service';

@Component({
  standalone: true,
  imports: [CommonModule, IconButtonComponent, EncodingFieldPillComponent],
  selector: 'mode-encoding-field-single',
  templateUrl: './encoding-field-single.component.html',
  styleUrls: ['./encoding-field-single.component.scss'],
  providers: [{ provide: UpdateFieldService, useClass: UpdateSingleFieldService }],
})
export class EncodingFieldSingleComponent {
  @Input({ required: true }) field!: EncField;
  @Input({ required: true }) encoding!: Encoding;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() format: ChartTypes.ChartFormat | undefined;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) viewName!: string;
  @Input({ required: true }) vizToken!: string;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;

  @Input() allowScalars = false;
  @Input() allowAggregates = false;
  @Input() allowQuickCalcs = false;
  @Input() allowContinuous = false;
  @Input() allowDiscrete = false;
  @Input() allowBinned = true;

  @Input() canLegendBeSplit = false;
  @Input() hasSplitLegend = false;
  @Input() showColorSettings = false;
  @Input() hideAllMenuOptions = false;

  @Output() update = new EventEmitter<{ $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }>();
  @Output() remove = new EventEmitter<{ $field: EncField }>();
  @Output() removeSort = new EventEmitter<{ $source: string }>();
  @Output() toggleDualAxis = new EventEmitter<{
    $field: CompiledField;
    $sibling: EncField | undefined;
    $channel: 'row' | 'column';
  }>();
  @Output() isValidField: EventEmitter<{ isValid: boolean; field: EncField }> = new EventEmitter<{
    isValid: boolean;
    field: EncField;
  }>();
  @Output() splitLegend = new EventEmitter<boolean>();
  @Output() getBinSize = new EventEmitter<{ columnName: string; token: string }>();

  onFieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    this.update.emit($event);
  }

  removeField(event$: { $field: EncField }): void {
    this.remove.emit(event$);
  }

  onRemoveSort(event$: { $source: string }): void {
    this.removeSort.emit(event$);
  }

  onToggleDualAxis(event$: { $field: CompiledField; $sibling: EncField | undefined; $channel: 'row' | 'column' }) {
    this.toggleDualAxis.emit(event$);
  }

  onSplitLegend(value: boolean) {
    this.splitLegend.emit(value);
  }

  onIsValidField($event: { isValid: boolean; field: EncField }) {
    this.isValidField.emit($event);
  }

  onGetBinSize($event: { columnName: string; token: string }) {
    this.getBinSize.emit($event);
  }
}
